'use client'

import React, { memo } from 'react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { isDefined } from '@segway/core'

import { CheckoutAffirmWidget } from '@/app/[locale]/checkout/_components/checkout/paymentMethods/checkoutAffirmWidget'
import { GoogleReCaptcha } from '@/components'
// import { ApplePay } from '@/components/applePay'
import { OpenPay } from '@/components'
import {
  AffirmItemIcon,
  AppleIcon,
  GooglePayItemIcon,
  PaypalItemIcon,
  VenmoItemIcon,
} from '@/components/icons'
import { PAYMENT_METHODS_CARD_IMAGES } from '@/constants'
import { Button, Checkbox, Form, FormControl, FormField, FormItem, FormMessage } from '@/ui'

import useCheckoutPaymentMethods from '../../../../../../hooks/checkout/useCheckoutPaymentMethods'

import BraintreeCreditCard from './braintreeCreditCard/BraintreeCreditCard'
import MethodItemWrapper from './methodItemWrapper/MethodItemWrapper'
import OpenPayCreditCard from './openPayCreditCard/OpenPayCreditCard'

type TMethod = { code: string; title: string } | undefined

interface IPaymentMethodsProps {
  methods: TMethod[] | undefined
}

/**
 * Payment Methods component
 *
 * @param {IPaymentMethodsProps} props - Component props
 * @param {array} [methods] - Payment method list
 *
 * @returns {JSX.Element} Payment methods component
 */
const PaymentMethods = ({ methods }: IPaymentMethodsProps) => {
  const {
    form,
    publicHash,
    cartPrices,
    methodsList,
    agreementRef,
    selectMethod,
    recaptchaData,
    currentMethod,
    cartAffirmData,
    methodDataReady,
    vaultPlaceOrder,
    vaultMethodsList,
    selectVaultMethod,
    handleCheckBefore,
    checkoutAgreement,
    openPayPlaceOrder,
    checkmoPlaceOrder,
    googleReCaptchaRef,
    placeOrderAvailable,
    googleReCaptchaToken,
    setGoogleReCaptchaToken,
    isGoogleRecaptchaInvisible,
    googleRecaptchaAsyncScriptOnLoad,
  } = useCheckoutPaymentMethods(methods)

  const t = useTranslations('Cart')

  return methodDataReady ? (
    <div className="rounded-[12px] bg-white px-3 py-4 xl:p-0">
      <div className="space-y-2 xl:space-y-4">
        <p className="mb-3 text-[16px] xl:text-[20px]">{t('payment_methods')}</p>
        {/* 谷歌人机验证 */}
        {isGoogleRecaptchaInvisible && (
          <>
            <GoogleReCaptcha
              ref={googleReCaptchaRef}
              rendering={recaptchaData?.recaptchaSetting?.rendering}
              onChange={(token) => {
                if (token) {
                  setGoogleReCaptchaToken(token)
                }
              }}
              asyncScriptOnLoad={googleRecaptchaAsyncScriptOnLoad}
            />
          </>
        )}

        {/* 协议 */}
        <Form {...form}>
          <form>
            <FormField
              control={form.control}
              name="checked"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div
                      ref={agreementRef}
                      className="flex min-h-[34px] scroll-mt-[100px] items-center pb-2 pt-2 xl:pb-0">
                      {checkoutAgreement && (
                        <>
                          <Checkbox
                            required
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                          <span
                            className="ml-1 text-[12px] xl:ml-2"
                            dangerouslySetInnerHTML={{
                              __html: checkoutAgreement,
                            }}
                          />
                        </>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
        <OpenPay />

        {vaultMethodsList.length > 0 && (
          <>
            {vaultMethodsList.map((item) => {
              const { code, title, public_hash } = item
              const isSelected = code === currentMethod && publicHash === public_hash
              const isPaypal = code === 'braintree_paypal_vault'
              const isCreditCard = code === 'braintree_cc_vault' && item?.cardType
              return (
                <MethodItemWrapper
                  key={public_hash}
                  handleCheckBefore={handleCheckBefore}
                  placeOrderAvailable={placeOrderAvailable}>
                  <div
                    onClick={() => {
                      selectVaultMethod(code, public_hash)
                    }}
                    className="cursor-pointer p-3 xl:p-5">
                    <div className="grid grid-cols-[1fr_auto] items-center gap-[40px]">
                      <div>
                        <p className="flex items-center xl:text-[16px]">{title}</p>
                        {!isSelected && (
                          <p className="mt-2 font-miSansRegular330 text-[12px] text-[#86868B] xl:text-[14px]">
                            {t('payment_after_placing')}
                          </p>
                        )}
                      </div>
                      <div className="relative mr-2 h-[32px] w-[48px] rounded border border-gray-1">
                        {/* 图标 */}
                        {isPaypal && (
                          <Image
                            src={PAYMENT_METHODS_CARD_IMAGES.PP}
                            fill
                            style={{ objectFit: 'contain' }}
                            alt={title}
                          />
                        )}
                        {isCreditCard && (
                          <Image
                            fill
                            src={
                              PAYMENT_METHODS_CARD_IMAGES[
                                item.cardType as keyof typeof PAYMENT_METHODS_CARD_IMAGES
                              ]
                            }
                            style={{ objectFit: 'contain' }}
                            alt={title}
                          />
                        )}
                      </div>
                    </div>
                    {isSelected && (
                      <Button className="mt-3" onClick={vaultPlaceOrder}>
                        {t('place_order')}
                      </Button>
                    )}
                  </div>
                </MethodItemWrapper>
              )
            })}
            <p className="pt-3 xl:text-[16px]">{t('select_payment_method')}</p>
          </>
        )}

        {methodsList?.map((method) => {
          if (method?.code && method?.title) {
            const { code, title } = method
            const isAffirm = code === 'affirm_gateway'
            const isCheckmo = code === 'checkmo'
            const isApple = code === 'braintree_applepay'
            const isOpenPay = code === 'openpay_cards'
            let methodTitle = isAffirm ? 'Installment payment' : title
            if (isOpenPay) {
              methodTitle = 'Openpay'
            }
            return (
              <MethodItemWrapper
                key={code}
                handleCheckBefore={handleCheckBefore}
                placeOrderAvailable={placeOrderAvailable}>
                <div
                  onClick={() => {
                    selectMethod(code)
                  }}
                  className="grid cursor-pointer grid-cols-[1fr_auto] items-center gap-[40px] p-3 xl:p-5">
                  <div>
                    <p className="xl:text-[16px]">{methodTitle}</p>
                    {isOpenPay && (
                      <p className="mt-2 font-miSansRegular330 text-[12px] text-[#86868B] xl:text-[14px]">
                        {title}
                      </p>
                    )}
                    {!isOpenPay && currentMethod !== code && (
                      <p className="mt-2 font-miSansRegular330 text-[12px] text-[#86868B] xl:text-[14px]">
                        {t('payment_after_placing')}
                      </p>
                    )}
                  </div>
                  {/* TODO: Method Icon */}
                  {code === 'braintree_paypal' && <PaypalItemIcon />}
                  {code === 'braintree_googlepay' && <GooglePayItemIcon />}
                  {code === 'braintree_venmo' && <VenmoItemIcon />}
                  {isApple && <AppleIcon />}
                  {isAffirm && <AffirmItemIcon />}
                </div>
                {/* TODO: Payment Method */}
                {code === currentMethod && (
                  <div className="px-3 xl:py-0">
                    {isAffirm ? (
                      <>
                        {/* Affirm */}
                        {isDefined(cartAffirmData) && cartPrices?.grand_total?.value && (
                          <div className="pb-6 xl:pl-2">
                            <CheckoutAffirmWidget
                              totalPrice={cartPrices?.grand_total?.value}
                              affirmData={cartAffirmData}
                              googleReCaptchaToken={googleReCaptchaToken}
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {isCheckmo ? (
                          <Button className="mb-2 xl:mb-4" onClick={checkmoPlaceOrder}>
                            {t('place_order')}
                          </Button>
                        ) : (
                          <>
                            {isOpenPay ? (
                              <>
                                <OpenPayCreditCard
                                  cartPrices={cartPrices}
                                  onTokenCreated={async (
                                    tokenId: string,
                                    deviceId: string,
                                    installments: number,
                                  ) => {
                                    openPayPlaceOrder(tokenId, deviceId, installments)
                                  }}
                                />
                              </>
                            ) : (
                              <BraintreeCreditCard
                                code={code}
                                googleReCaptchaToken={googleReCaptchaToken}
                                selectMethod={selectMethod}
                              />
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </MethodItemWrapper>
            )
          }
        })}
      </div>
    </div>
  ) : null
}

export default memo(PaymentMethods)
